import {
  useContext,
  useEffect,
  useRef,
  useState,
  type FC,
  type MouseEvent,
} from "react";
import { Box } from "@mui/material";
import {
  embedDashboard,
  type EmbeddedDashboard,
} from "@superset-ui/embedded-sdk";
import type { AxiosError } from "axios";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { triggerDownload, useSupersetKeepalive } from "@thalamos/frontend";
import { canViewSupersetDashboards } from "@thalamos/aspire-common";

import {
  SupersetDashboardPageInner,
  type SupersetDashboardPageInnerProps,
} from "./SupersetDashboardPageInner.js";
import { api, apiHooks, logout } from "~/api.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { routeFns } from "~/routePaths.js";

export type { SupersetDashboardPageInnerProps };
export { SupersetDashboardPageInner };

/**
 * Decides whether to show the dashboard or an access message based on the
 * signed-in user's role.
 *
 * The data-fetching hook lives in {@link AuthorizedSupersetDashboardPage} so it
 * is only ever mounted (and only ever called) for dashboard viewers. Gating it
 * with a conditional hook call instead would break the Rules of Hooks if the
 * user switches team/context without a full page reload.
 */
export const SupersetDashboardPage: FC = () => {
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user;
  const { dashboardId: supersetDashboardId } = useParams();

  if (!canViewSupersetDashboards(user)) {
    return <NotADashboardViewer />;
  }

  if (!supersetDashboardId) {
    return <Navigate to={routeFns.dashboard()} replace />;
  }

  return (
    <AuthorizedSupersetDashboardPage
      organisationId={user?.sessionContext?.organisationId ?? ""}
      supersetDashboardId={supersetDashboardId}
    />
  );
};

/**
 * Shown when the signed-in user is not under a Superset dashboard team — e.g.
 * they opened a bookmarked dashboard URL while signed in under a different team.
 */
export const NotADashboardViewer: FC = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      p: 4,
    }}
  >
    You are not currently signed in under a dashboard team. Please change your
    team to view dashboards.
  </Box>
);

const AuthorizedSupersetDashboardPage: FC<{
  organisationId: string;
  supersetDashboardId: string;
}> = ({ organisationId, supersetDashboardId }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const embeddedDashboardRef = useRef<EmbeddedDashboard | null>(null);
  const navigate = useNavigate();

  const [{ data: supersetDashboardData, loading, error }] =
    apiHooks.reports.supersetDashboardData(organisationId, supersetDashboardId);

  // Download menu state
  const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const downloadMenuOpen = Boolean(downloadAnchorEl);

  const handleDownloadClick = (event: MouseEvent<HTMLButtonElement>) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
  };

  const handleExport = async (format: "pdf" | "png") => {
    handleDownloadClose();
    setIsExporting(true);
    setExportError(null);

    try {
      // Get the currently active tabs from the embedded dashboard
      // Wrapped in try/catch to gracefully degrade if tab detection fails
      let activeTabs: string[] | undefined;
      let anchor: string | undefined;
      let dataMask: Record<string, unknown> | undefined;
      if (embeddedDashboardRef.current) {
        try {
          activeTabs = await embeddedDashboardRef.current.getActiveTabs();
          anchor = activeTabs[0];
        } catch (err) {
          // If we can't determine the active tabs (e.g. dashboard not initialised),
          // fall back to exporting the default dashboard instead of failing the export.
          console.error("Failed to get active Superset tabs:", err);
        }
        try {
          dataMask = await embeddedDashboardRef.current.getDataMask();
        } catch (err) {
          // If we can't retrieve the current filter state, fall back to
          // exporting without filters rather than failing the export.
          console.error("Failed to get Superset dataMask:", err);
        }
      }

      const response = await api.reports.exportSupersetDashboard(
        organisationId,
        supersetDashboardId,
        format,
        activeTabs,
        anchor,
        dataMask,
      );
      const { data, contentType, filename } = response.data;

      // Convert base64 to data URI and trigger download
      const dataUri = `data:${contentType};base64,${data}`;
      triggerDownload(dataUri, filename);
    } catch (err) {
      const axiosError = err as AxiosError<{ reason?: string }>;
      const message =
        axiosError.response?.data?.reason ??
        axiosError.message ??
        "Failed to export dashboard";
      setExportError(message);
      console.error("Failed to export dashboard:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const errorMessage = error
    ? ((error as AxiosError<{ reason?: string }>).response?.data?.reason ??
      (error as AxiosError).message ??
      "Failed to load dashboard")
    : null;

  const { keepaliveDialog } = useSupersetKeepalive({
    onKeepalive: async () => {
      // Calling /api/user/me extends the server-side session.
      await api.users.me(null);
    },
    onTimeout: logout,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!supersetDashboardData || !container || error) {
      return;
    }

    const { dashboardId, guestToken, supersetUrl } = supersetDashboardData;

    if (!dashboardId || !guestToken || !supersetUrl) {
      return;
    }

    let isMounted = true;

    embedDashboard({
      id: dashboardId,
      supersetDomain: supersetUrl,
      mountPoint: container,
      fetchGuestToken: () => Promise.resolve(guestToken),
      dashboardUiConfig: {
        hideTitle: true,
        hideChartControls: false,
        filters: {
          expanded: true,
        },
      },
    })
      .then((dashboard) => {
        if (isMounted) {
          embeddedDashboardRef.current = dashboard;
        } else {
          dashboard.unmount();
        }
      })
      .catch((err: unknown) => {
        console.error("Failed to embed Superset dashboard:", err);
      });

    return () => {
      isMounted = false;
      if (embeddedDashboardRef.current) {
        embeddedDashboardRef.current.unmount();
        embeddedDashboardRef.current = null;
      }
    };
  }, [supersetDashboardData, error]);

  if (loading) {
    return (
      <SupersetDashboardPageInner
        loading={true}
        errorMessage={null}
        hasValidDashboardData={false}
        isExporting={false}
        exportError={null}
        downloadMenuOpen={false}
        downloadAnchorEl={null}
        onDownloadClick={handleDownloadClick}
        onDownloadClose={handleDownloadClose}
        onExport={handleExport}
        containerRef={containerRef}
      />
    );
  }

  const hasValidDashboardData = Boolean(
    supersetDashboardData?.dashboardId &&
    supersetDashboardData?.guestToken &&
    supersetDashboardData?.supersetUrl,
  );

  return (
    <SupersetDashboardPageInner
      loading={false}
      errorMessage={errorMessage}
      hasValidDashboardData={hasValidDashboardData}
      title={supersetDashboardData?.displayName}
      isExporting={isExporting}
      exportError={exportError}
      downloadMenuOpen={downloadMenuOpen}
      downloadAnchorEl={downloadAnchorEl}
      onDownloadClick={handleDownloadClick}
      onDownloadClose={handleDownloadClose}
      onExport={handleExport}
      onViewGlossary={
        supersetDashboardData?.supersetDashboardId
          ? () => {
              navigate(
                routeFns.dataGlossary(
                  // oxlint-disable-next-line @typescript-eslint/no-non-null-assertion -- guarded by ternary condition
                  supersetDashboardData.supersetDashboardId!,
                ),
              );
            }
          : undefined
      }
      containerRef={containerRef}
      keepaliveDialog={keepaliveDialog}
    />
  );
};
